import {
  Box,
  Button,
  ButtonProps,
  capitalize,
  CircularProgress,
  makeStyles,
  Popover,
  SvgIconProps,
  Theme,
  Typography,
} from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import clsx from 'clsx';
import {
  cloneElement,
  forwardRef,
  isValidElement,
  memo,
  ReactElement,
  ReactNode,
  Ref,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { WbDivider } from '../WbDivider';

export type Variant = 'text' | 'outlined' | 'contained';

export type ButtonAccessibilityProps =
  | { icon: ReactElement<SvgIconProps>; label: ReactNode }
  | { icon: ReactElement<SvgIconProps>; 'aria-label': string }
  | { icon?: undefined };

export type WbButtonProps = Omit<ButtonProps, 'children'> &
  ButtonAccessibilityProps & {
    icon?: ReactElement<SvgIconProps>;
    iconColor?: string;
    label?: ReactNode;
    loading?: boolean;
    variant?: Variant;
    selectProps?: SelectProps;
    select?: boolean;
    to?: string;
    fullWidthUnderBreakpoint?: Breakpoint;
  };

type UseStylesProps = {
  fullWidthUnderBreakpoint?: Breakpoint;
};

const useStyles = makeStyles<Theme, UseStylesProps>((theme: Theme) => ({
  label: {
    fontWeight: 500,
  },
  text: {
    padding: 0,
    minWidth: 0,
    ...theme.typography.body2,
    fontWeight: 400,
    textUnderlineOffset: `${theme.spacing(0.5)}px`,
  },
  underline: {
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  onlyIcon: {
    minWidth: 0,
    borderRadius: '50%',
    padding: '4px',
  },
  option: {
    padding: '8px',
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.bkg.primary,
    },
  },
  fullWidthUnderBreakpoint: ({ fullWidthUnderBreakpoint }) => ({
    ...(fullWidthUnderBreakpoint && {
      [`@container (max-width: ${theme.breakpoints.values[fullWidthUnderBreakpoint]}px)`]:
        {
          width: '100%',
        },
    }),
  }),
}));

interface SelectProps {
  options: any[];
  optionClick: (value?: any) => void;
}

export const WbButtonComponent = (
  {
    loading = false,
    icon,
    label,
    onClick,
    color = 'primary',
    iconColor,
    variant = 'contained',
    style,
    select = false,
    selectProps,
    className,
    to,
    fullWidth = false,
    fullWidthUnderBreakpoint,
    ...props
  }: WbButtonProps,
  ref: Ref<HTMLButtonElement>,
) => {
  const classes = useStyles({ fullWidthUnderBreakpoint });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClickOnSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseOnSelect = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const onlyIcon = icon && !label;

  return (
    <Button
      {...(to ? { component: Link, to } : {})}
      onClick={onClick}
      variant={variant}
      color={color}
      size="small"
      ref={ref}
      classes={{
        text: clsx(
          classes.text,
          !icon && !isValidElement(label) && classes.underline,
        ),
      }}
      className={clsx(
        className,
        onlyIcon && classes.onlyIcon,
        fullWidthUnderBreakpoint && classes.fullWidthUnderBreakpoint,
      )}
      style={{ position: 'relative', whiteSpace: 'nowrap', ...style }}
      {...props}
    >
      {loading && (
        <Box
          style={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <CircularProgress
            size={20}
            thickness={8}
            color="secondary"
            style={{ color: 'inherit' }}
          />
        </Box>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          visibility: loading ? 'hidden' : 'visible',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        {icon &&
          cloneElement(icon, {
            htmlColor: iconColor ?? 'inherit',
            fontSize: 'small',
          })}

        {label &&
          (isValidElement(label) ? (
            label
          ) : (
            <Typography
              className={clsx(
                variant === 'text' ? classes.text : classes.label,
                !icon && variant === 'text' && classes.underline,
              )}
              style={{ marginLeft: icon ? '4px' : '0px' }}
            >
              {label}
            </Typography>
          ))}
        {select && (
          <>
            <WbDivider height="20px" />
            <Box
              display="flex"
              alignItems="center"
              onClick={handleClickOnSelect}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Box>
            {selectProps && (
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleCloseOnSelect}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  {selectProps.options?.map(op => (
                    <Box
                      className={classes.option}
                      onClick={() => {
                        selectProps.optionClick(op);
                        handleCloseOnSelect();
                      }}
                    >
                      {capitalize(op)}
                    </Box>
                  ))}
                </Box>
              </Popover>
            )}
          </>
        )}
      </div>
    </Button>
  );
};

export const WbButton = memo(
  forwardRef(WbButtonComponent),
) as typeof WbButtonComponent;
